// =============================================================================
// PEDRA — src/components/EditableGrid.tsx
// Grade editavel generica, no estilo de uma planilha, usada pelas tabelas de
// pecas e de chapas. Nao conhece o dominio: tudo entra por `ColumnDef`.
//
// Recursos:
//   - celula entra em edicao ao focar + digitar, com F2 ou com Enter;
//   - navegacao por SETAS, Home/End, PageUp/PageDown;
//   - a grade inteira e UMA parada de Tab (roving tabindex): Tab e Shift+Tab
//     saem da grade para o proximo/anterior controle da pagina;
//   - linha vazia mantida automaticamente no fim (como no CutList Optimizer);
//   - colar do Excel (TAB), do Google Sheets e de CSV (";" ou ",");
//   - marcacao de linha invalida, de linha destacada e de chapa ja usada;
//   - acessibilidade: role="grid", aria-rowindex/colindex, aria-invalid e
//     mensagem de erro anunciada (role="alert" + aria-describedby).
// =============================================================================

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactElement } from 'react';
import { PART_COLORS } from '../core/defaults';
import type { Micron, Unit } from '../core/types';
import {
  MICRON_PER_FOOT,
  MICRON_PER_INCH,
  MICRON_PER_UNIT,
  formatFraction,
  isImperial,
  parseDimension,
} from '../core/units';

// -----------------------------------------------------------------------------
// CONTRATO PUBLICO
// -----------------------------------------------------------------------------

/** Tipo de editor de uma coluna. */
export type CellEditor =
  | 'text'
  | 'dimension'
  | 'integer'
  | 'checkbox'
  | 'select'
  | 'color'
  | 'grain'
  | 'edgebanding';

export interface ColumnDef<T> {
  /** Chave unica dentro da tabela. */
  key: string;
  /** Rotulo pt-BR exibido no cabecalho. */
  header: string;
  /** Valor usado em `grid-template-columns`, ex. 'minmax(80px,1fr)'. */
  width: string;
  align?: 'start' | 'center' | 'end';
  editor: CellEditor;
  /** Opcoes do editor 'select'. */
  options?: ReadonlyArray<{ value: string; label: string }>;
  /** Texto exibido em modo leitura. */
  format(row: T): string;
  /** Texto colocado no input ao entrar em edicao. Padrao = `format(row)`. */
  toInput?(row: T): string;
  /** Converte o texto digitado num patch da linha. `null` = entrada invalida. */
  parse(raw: string, row: T): Partial<T> | null;
  /** Mensagem de erro da celula, em pt-BR, ou `null`. */
  validate?(row: T): string | null;
  /** Padrao: true. */
  editable?: boolean;
  /** Tooltip do cabecalho. */
  title?: string;
  /**
   * Mensagem exibida (e anunciada) quando o texto digitado nao e aceito.
   * Deve explicar o FORMATO esperado, em pt-BR. Sem ela, a grade usa um
   * texto padrao de acordo com o editor da coluna.
   */
  errorHint?: string;

  // --- extensoes locais (opcionais, compativeis com o contrato) --------------
  /** Sugestoes de um `<datalist>` para o editor 'text' (ex.: materiais). */
  datalist?: readonly string[];
  /**
   * Teclado que o celular abre nesta coluna. Padrao: 'numeric' no editor
   * 'integer', 'text' no resto.
   *
   * Por que nao deduzir 'decimal' de todo editor 'dimension': o teclado
   * decimal do iPhone tem digitos e UM separador, e mais nada — quem trabalha
   * em polegada precisa digitar `16 1/2"`, com espaco, barra e aspas. Cabe a
   * quem monta a coluna decidir, porque so ela conhece a unidade do projeto.
   */
  inputMode?: 'text' | 'numeric' | 'decimal';
  /**
   * Acao disparada por clique/Enter em editores que abrem algo fora da grade
   * (usado pelo editor 'edgebanding', que abre o modal de fita).
   */
  onActivate?(row: T): void;
}

export interface EditableGridProps<T> {
  columns: ReadonlyArray<ColumnDef<T>>;
  rows: readonly T[];
  getRowId(row: T): string;
  onChangeRow(id: string, patch: Partial<T>): void;
  onAddRow(): void;
  onDeleteRows(ids: string[]): void;
  onDuplicateRows(ids: string[]): void;
  /** Texto TSV/CSV vindo do clipboard, ja bruto. */
  onPaste(text: string): void;
  onReorder?(ids: string[], toIndex: number): void;
  selectedIds: readonly string[];
  onSelectionChange(ids: string[]): void;
  /** Erros por linha: `{ [rowId]: string[] }`. */
  rowIssues?: Readonly<Record<string, string[]>>;
  emptyMessage: string;
  ariaLabel: string;
  highlightedRowId?: string | null;
  onHoverRow?(id: string | null): void;

  // --- extensoes locais (opcionais) -----------------------------------------
  /** Quando informado, a grade mantem sempre uma linha em branco no fim. */
  isRowEmpty?(row: T): boolean;
  /** Linhas ja consumidas pelo resultado (pintadas de cinza). */
  usedRowIds?: readonly string[];
  /** Teto de linhas para o preenchimento automatico. Padrao 5000. */
  maxRows?: number;
}

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

interface CellPos {
  r: number;
  c: number;
}

/** Texto que vai para o input ao entrar em edicao. */
function inputTextOf<T>(col: ColumnDef<T>, row: T): string {
  return col.toInput ? col.toInput(row) : col.format(row);
}

/**
 * Le um inteiro >= 0 digitado pelo usuario, tolerando ponto de milhar e
 * espacos ("1.200" = 1200). Devolve `null` quando o texto nao e um inteiro.
 * Texto vazio devolve `null` — cabe a cada coluna decidir o que fazer com ele.
 */
export function parseIntegerText(raw: string): number | null {
  const clean = raw.replace(/[.\s ]/g, '').trim();
  if (clean === '' || !/^\d+$/.test(clean)) return null;
  const n = Number(clean);
  return Number.isSafeInteger(n) ? n : null;
}

// -----------------------------------------------------------------------------
// MEDIDA SEM PERDA DE PRECISAO
//
// A grade e a fonte da verdade do que o usuario digitou: ela NUNCA arredonda.
// `settings.decimalPlaces` continua governando o diagrama, as estatisticas e os
// relatorios, mas nas tabelas o valor aparece inteiro — so os zeros a direita
// sao aparados (3,07 · 2750 · 16,5 · 0,3).
// -----------------------------------------------------------------------------

/** Denominadores testados na saida imperial, do mais grosso ao mais fino. */
const IMPERIAL_DENOMINATORS = [1, 2, 4, 8, 16, 32, 64] as const;

/** true quando `text` volta para exatamente o mesmo valor em Micron. */
function roundTrips(text: string, unit: Unit, v: number): boolean {
  const back = parseDimension(text, unit);
  return back !== null && Math.abs(back - v) < 0.5;
}

/** Metrico: divisao exata em inteiros (1 µm = 0,001 mm), sem float sujo. */
function metricExact(v: number, unit: Unit): string {
  const factor = MICRON_PER_UNIT[unit];
  const negative = v < 0;
  const abs = Math.abs(Math.round(v));
  const digits = String(factor).length - 1;
  const int = Math.floor(abs / factor);
  const frac = String(abs % factor)
    .padStart(digits, '0')
    .replace(/0+$/, '');
  return `${negative ? '-' : ''}${int}${frac === '' ? '' : `,${frac}`}`;
}

/**
 * Imperial: usa a fracao MAIS SIMPLES que reproduz o valor exato (1/2 … 1/64) e,
 * se nenhuma reproduzir, cai para polegadas decimais — que sempre fecham em 6
 * casas (0,000001" = 0,0254 µm).
 */
function imperialExact(v: number, unit: Unit): string {
  for (const den of IMPERIAL_DENOMINATORS) {
    const text = formatFraction(v, unit, den);
    if (roundTrips(text, unit, v)) return text;
  }
  const negative = v < 0;
  const abs = Math.abs(v);
  const feet = unit === 'ft' ? Math.floor(abs / MICRON_PER_FOOT) : 0;
  const rest = abs - feet * MICRON_PER_FOOT;
  let text = '';
  for (let dp = 1; dp <= 6; dp++) {
    const decimal = (rest / MICRON_PER_INCH)
      .toFixed(dp)
      .replace(/0+$/, '')
      .replace(/\.$/, '')
      .replace('.', ',');
    text = `${negative ? '-' : ''}${feet > 0 ? `${feet}' ` : ''}${decimal}"`;
    if (roundTrips(text, unit, v)) return text;
  }
  return text;
}

/**
 * Micron -> texto da grade, na unidade do projeto e SEM perda: o que sai daqui,
 * relido por `parseDimension`, devolve o mesmo Micron. Sem separador de milhar,
 * justamente para nao criar ambiguidade ao reentrar na celula ("2.750").
 */
export function formatDimensionExact(v: Micron, unit: Unit): string {
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '0';
  return isImperial(unit) ? imperialExact(v, unit) : metricExact(v, unit);
}

// -----------------------------------------------------------------------------

/** Mensagem padrao de formato, por tipo de editor. */
const DEFAULT_ERROR_HINT: Readonly<Record<CellEditor, string>> = {
  text: 'Valor inválido para esta coluna.',
  dimension:
    'Medida inválida. Digite um número com vírgula decimal (ex.: 3,07) ou uma fração de polegada (ex.: 16 1/2").',
  integer: 'Valor inválido. Digite um número inteiro, sem vírgula (ex.: 12).',
  checkbox: 'Valor inválido.',
  select: 'Escolha uma das opções da lista.',
  color: 'Cor inválida. Use o formato #RRGGBB (ex.: #A8D3E8).',
  grain: 'Valor inválido.',
  edgebanding: 'Valor inválido.',
};

/** true quando o editor e ativado por clique/Enter em vez de digitacao. */
function isToggleEditor(editor: CellEditor): boolean {
  return editor === 'checkbox' || editor === 'grain' || editor === 'color' || editor === 'edgebanding';
}

/** Glifo do ciclo de veio: 0 indiferente · 1 largura · 2 comprimento. */
const GRAIN_GLYPH = ['✳', '↔', '↕'] as const;
const GRAIN_TITLE = [
  'Veio: indiferente (a peça pode girar 90°)',
  'Veio: ao longo da largura',
  'Veio: ao longo do comprimento',
] as const;

/** Lados da fita, na ordem da mascara "topo, direita, base, esquerda". */
const BAND_SIDES = ['topo', 'direita', 'base', 'esquerda'] as const;

// -----------------------------------------------------------------------------
// COMPONENTE
// -----------------------------------------------------------------------------

export function EditableGrid<T>(props: EditableGridProps<T>): ReactElement {
  const {
    columns,
    rows,
    getRowId,
    onChangeRow,
    onAddRow,
    onDeleteRows,
    onDuplicateRows,
    onPaste,
    onReorder,
    selectedIds,
    onSelectionChange,
    rowIssues,
    emptyMessage,
    ariaLabel,
    highlightedRowId,
    onHoverRow,
    isRowEmpty,
    usedRowIds,
    maxRows = 5000,
  } = props;

  const [active, setActive] = useState<CellPos>({ r: 0, c: 0 });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState(false);
  const [colorCell, setColorCell] = useState<string | null>(null);
  const domId = useId();

  const rowCount = rows.length;
  const colCount = columns.length;
  const activeR = rowCount === 0 ? 0 : Math.max(0, Math.min(active.r, rowCount - 1));
  const activeC = colCount === 0 ? 0 : Math.max(0, Math.min(active.c, colCount - 1));

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const usedSet = useMemo(() => new Set(usedRowIds ?? []), [usedRowIds]);
  // Colunas fixas das pontas: caixa de selecao e botoes de acao. Sao estreitas
  // de proposito — cada pixel aqui e um pixel a menos para os cabecalhos.
  const template = useMemo(
    () => ['22px', ...columns.map((c) => c.width), '46px'].join(' '),
    [columns],
  );

  // --- foco ------------------------------------------------------------------
  const cellRefs = useRef(new Map<string, HTMLDivElement>());
  const wantFocus = useRef(false);
  const selectAll = useRef(true);

  const registerCell = useCallback((key: string, el: HTMLDivElement | null) => {
    if (el) cellRefs.current.set(key, el);
    else cellRefs.current.delete(key);
  }, []);

  useEffect(() => {
    if (!wantFocus.current) return;
    wantFocus.current = false;
    if (editing) return; // o input assume o foco sozinho
    const el = cellRefs.current.get(`${activeR}:${activeC}`);
    if (el) el.focus();
  });

  const editorRef = useCallback((el: HTMLInputElement | HTMLSelectElement | null) => {
    if (!el) return;
    el.focus();
    if (selectAll.current && el instanceof HTMLInputElement) el.select();
    selectAll.current = true;
  }, []);

  // --- linha vazia automatica no fim ----------------------------------------
  const appendedFor = useRef<readonly T[] | null>(null);
  useEffect(() => {
    if (!isRowEmpty) return;
    if (rows.length >= maxRows) return;
    if (appendedFor.current === rows) return;
    const last = rows.length > 0 ? rows[rows.length - 1] : null;
    if (last === null || !isRowEmpty(last)) {
      appendedFor.current = rows;
      onAddRow();
    }
  }, [rows, isRowEmpty, maxRows, onAddRow]);

  // --- operacoes basicas -----------------------------------------------------
  const focusCell = useCallback(
    (r: number, c: number) => {
      const nr = rowCount === 0 ? 0 : Math.max(0, Math.min(r, rowCount - 1));
      const nc = colCount === 0 ? 0 : Math.max(0, Math.min(c, colCount - 1));
      setActive({ r: nr, c: nc });
      setEditing(false);
      setDraftError(false);
      setColorCell(null);
      wantFocus.current = true;
    },
    [rowCount, colCount],
  );

  const startEdit = useCallback(
    (r: number, c: number, initial?: string) => {
      if (r < 0 || r >= rows.length || c < 0 || c >= columns.length) return;
      const col = columns[c];
      const row = rows[r];
      if (col.editable === false) return;
      selectAll.current = initial === undefined;
      setActive({ r, c });
      setDraft(initial === undefined ? inputTextOf(col, row) : initial);
      setDraftError(false);
      setEditing(true);
    },
    [columns, rows],
  );

  const applyPatch = useCallback(
    (r: number, patch: Partial<T> | null): boolean => {
      if (!patch || r < 0 || r >= rows.length) return false;
      onChangeRow(getRowId(rows[r]), patch);
      return true;
    },
    [rows, getRowId, onChangeRow],
  );

  /** Confirma a edicao. Devolve false quando o texto e invalido. */
  const commitEdit = useCallback(
    (move: 'down' | 'up' | 'right' | 'none'): boolean => {
      if (activeR >= rows.length || activeC >= columns.length) return false;
      const col = columns[activeC];
      const row = rows[activeR];
      const patch = col.parse(draft, row);
      if (!patch) {
        setDraftError(true);
        return false;
      }
      onChangeRow(getRowId(row), patch);
      setDraftError(false);
      setEditing(false);
      wantFocus.current = true;
      if (move === 'down') focusCell(activeR + 1, activeC);
      else if (move === 'up') focusCell(activeR - 1, activeC);
      else if (move === 'right') focusCell(activeR, activeC + 1);
      return true;
    },
    [columns, rows, activeR, activeC, draft, getRowId, onChangeRow, focusCell],
  );

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraftError(false);
    wantFocus.current = true;
  }, []);

  /** Clique/Enter numa celula: alterna, cicla, abre paleta ou entra em edicao. */
  const activateCell = useCallback(
    (r: number, c: number) => {
      if (r < 0 || r >= rows.length || c < 0 || c >= columns.length) return;
      const col = columns[c];
      const row = rows[r];
      if (col.editable === false) return;
      switch (col.editor) {
        case 'checkbox': {
          const current = inputTextOf(col, row).trim();
          const on = current === '1' || current === 'true' || current === 'sim';
          applyPatch(r, col.parse(on ? '0' : '1', row));
          break;
        }
        case 'grain': {
          const current = Number(inputTextOf(col, row)) || 0;
          applyPatch(r, col.parse(String((current + 1) % 3), row));
          break;
        }
        case 'color':
          setColorCell(`${r}:${c}`);
          break;
        case 'edgebanding':
          if (col.onActivate) col.onActivate(row);
          break;
        default:
          startEdit(r, c);
          break;
      }
    },
    [columns, rows, applyPatch, startEdit],
  );

  // --- selecao ---------------------------------------------------------------
  const toggleRowSelection = useCallback(
    (id: string) => {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange([...next]);
    },
    [selected, onSelectionChange],
  );

  const allSelected = rowCount > 0 && rows.every((row) => selected.has(getRowId(row)));

  const toggleSelectAll = useCallback(() => {
    if (allSelected) onSelectionChange([]);
    else onSelectionChange(rows.map((row) => getRowId(row)));
  }, [allSelected, rows, getRowId, onSelectionChange]);

  /** Linhas alvo das acoes em lote: a selecao, ou a linha ativa. */
  const targetIds = useCallback((): string[] => {
    if (selectedIds.length > 0) return [...selectedIds];
    if (activeR >= rows.length) return [];
    return [getRowId(rows[activeR])];
  }, [selectedIds, rows, activeR, getRowId]);

  const moveRow = useCallback(
    (delta: number) => {
      if (!onReorder || activeR >= rows.length) return;
      const to = activeR + delta;
      if (to < 0 || to >= rowCount) return;
      onReorder([getRowId(rows[activeR])], to);
      setActive({ r: to, c: activeC });
      wantFocus.current = true;
    },
    [onReorder, rows, activeR, activeC, rowCount, getRowId],
  );

  // --- teclado (fora do modo edicao) ----------------------------------------
  const handleGridKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (editing) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key;

      if (ctrl && key === 'Enter') {
        e.preventDefault();
        onAddRow();
        return;
      }
      if (ctrl && (key === 'Delete' || key === 'Backspace')) {
        e.preventDefault();
        const ids = targetIds();
        if (ids.length > 0) onDeleteRows(ids);
        onSelectionChange([]);
        return;
      }
      if (ctrl && e.shiftKey && (key === 'D' || key === 'd')) {
        e.preventDefault();
        const ids = targetIds();
        if (ids.length > 0) onDuplicateRows(ids);
        return;
      }
      if (ctrl && (key === 'A' || key === 'a')) {
        e.preventDefault();
        toggleSelectAll();
        return;
      }
      if (e.altKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
        e.preventDefault();
        moveRow(key === 'ArrowUp' ? -1 : 1);
        return;
      }

      switch (key) {
        case 'ArrowUp':
          e.preventDefault();
          focusCell(activeR - 1, activeC);
          return;
        case 'ArrowDown':
          e.preventDefault();
          focusCell(activeR + 1, activeC);
          return;
        case 'ArrowLeft':
          e.preventDefault();
          focusCell(activeR, activeC - 1);
          return;
        case 'ArrowRight':
          e.preventDefault();
          focusCell(activeR, activeC + 1);
          return;
        case 'Home':
          e.preventDefault();
          if (ctrl) focusCell(0, 0);
          else focusCell(activeR, 0);
          return;
        case 'End':
          e.preventDefault();
          if (ctrl) focusCell(rowCount - 1, colCount - 1);
          else focusCell(activeR, colCount - 1);
          return;
        case 'PageUp':
          e.preventDefault();
          focusCell(activeR - 10, activeC);
          return;
        case 'PageDown':
          e.preventDefault();
          focusCell(activeR + 10, activeC);
          return;
        // Tab NAO e tratado aqui de proposito: a grade e uma unica parada de
        // Tab (roving tabindex — so a celula ativa tem tabIndex 0), entao
        // Tab / Shift+Tab levam o foco para o proximo controle da PAGINA.
        // A navegacao entre celulas e feita pelas setas.
        case 'Enter':
          e.preventDefault();
          activateCell(activeR, activeC);
          return;
        case 'F2':
          e.preventDefault();
          startEdit(activeR, activeC);
          return;
        case 'Escape':
          if (selectedIds.length > 0) {
            e.preventDefault();
            onSelectionChange([]);
          }
          return;
        case ' ': {
          e.preventDefault();
          if (activeC >= columns.length || activeR >= rows.length) return;
          const col = columns[activeC];
          if (isToggleEditor(col.editor)) activateCell(activeR, activeC);
          else toggleRowSelection(getRowId(rows[activeR]));
          return;
        }
        case 'Delete':
        case 'Backspace': {
          if (activeC >= columns.length || activeR >= rows.length) return;
          const col = columns[activeC];
          if (col.editable === false || isToggleEditor(col.editor)) return;
          e.preventDefault();
          applyPatch(activeR, col.parse('', rows[activeR]));
          return;
        }
        default:
          break;
      }

      // Digitar um caractere imprimivel comeca a edicao substituindo o valor.
      if (!ctrl && !e.altKey && key.length === 1) {
        const col = columns[activeC];
        if (!col || col.editable === false) return;
        if (isToggleEditor(col.editor)) return;
        e.preventDefault();
        startEdit(activeR, activeC, key);
      }
    },
    [
      editing,
      activeR,
      activeC,
      rowCount,
      colCount,
      columns,
      rows,
      selectedIds,
      getRowId,
      focusCell,
      startEdit,
      activateCell,
      applyPatch,
      moveRow,
      targetIds,
      toggleSelectAll,
      toggleRowSelection,
      onAddRow,
      onDeleteRows,
      onDuplicateRows,
      onSelectionChange,
    ],
  );

  // --- teclado (dentro do input de edicao) ----------------------------------
  const handleEditorKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      const key = e.key;
      if (key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cancelEdit();
        return;
      }
      if (key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        commitEdit(e.shiftKey ? 'up' : 'down');
        return;
      }
      if (key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        if (commitEdit('none')) {
          if (e.shiftKey) {
            if (activeC === 0) focusCell(activeR - 1, colCount - 1);
            else focusCell(activeR, activeC - 1);
          } else if (activeC === colCount - 1) {
            focusCell(activeR + 1, 0);
          } else {
            focusCell(activeR, activeC + 1);
          }
        }
        return;
      }
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        commitEdit(key === 'ArrowUp' ? 'up' : 'down');
        return;
      }
      // As setas laterais movem o cursor dentro do texto: nada a fazer aqui.
      e.stopPropagation();
    },
    [cancelEdit, commitEdit, focusCell, activeR, activeC, colCount],
  );

  // --- colar do Excel --------------------------------------------------------
  /**
   * Remove as linhas em branco do FIM antes de colar. Sem isso a linha vazia
   * automatica ficaria encalhada no meio da tabela, entre o que o usuario ja
   * havia digitado e o bloco recem-colado. O efeito acima recria uma unica
   * linha vazia no fim assim que as linhas novas chegam.
   */
  const dropTrailingBlankRows = useCallback(() => {
    if (!isRowEmpty) return;
    const ids: string[] = [];
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i];
      if (!isRowEmpty(row)) break;
      ids.push(getRowId(row));
    }
    if (ids.length > 0) onDeleteRows(ids);
  }, [rows, isRowEmpty, getRowId, onDeleteRows]);

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (editing) return;
      const text = e.clipboardData.getData('text/plain');
      if (!text || text.trim() === '') return;
      e.preventDefault();
      dropTrailingBlankRows();
      onPaste(text);
    },
    [editing, onPaste, dropTrailingBlankRows],
  );

  const emitHover = useCallback(
    (id: string | null) => {
      if (onHoverRow) onHoverRow(id);
    },
    [onHoverRow],
  );

  // ---------------------------------------------------------------------------
  // RENDER — CELULA
  // ---------------------------------------------------------------------------
  const draftErrorId = `${domId}-draft-error`;

  /** Mensagem de formato da coluna, exibida quando o texto e recusado. */
  function errorHintOf(col: ColumnDef<T>): string {
    return col.errorHint ?? DEFAULT_ERROR_HINT[col.editor];
  }

  function renderCellContent(row: T, r: number, col: ColumnDef<T>, c: number): ReactElement {
    const isEditing = editing && r === activeR && c === activeC;
    const readOnly = col.editable === false;

    if (isEditing && col.editor === 'select') {
      return (
        <select
          ref={editorRef}
          className={`grid-editor grid-editor--select${draftError ? ' grid-editor--error' : ''}`}
          value={draft}
          aria-label={col.header}
          onChange={(e) => {
            setDraft(e.target.value);
            const patch = col.parse(e.target.value, row);
            if (patch) {
              onChangeRow(getRowId(row), patch);
              setEditing(false);
              wantFocus.current = true;
            } else {
              setDraftError(true);
            }
          }}
          onKeyDown={handleEditorKeyDown}
          onBlur={cancelEdit}
        >
          {(col.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (isEditing) {
      const listId = col.datalist ? `${domId}-dl-${col.key}` : undefined;
      return (
        <>
          <input
            ref={editorRef}
            className={`grid-editor${draftError ? ' grid-editor--error' : ''}`}
            value={draft}
            list={listId}
            inputMode={col.inputMode ?? (col.editor === 'integer' ? 'numeric' : 'text')}
            // Sem isto o celular capitaliza e "corrige" a medida digitada, e a
            // tecla de confirmacao vem escrita "ir" em vez de "seguinte".
            autoCapitalize="off"
            autoCorrect="off"
            // Nas colunas com `datalist` (material) o atributo fica de fora:
            // `autocomplete="off"` ja escondeu a lista de sugestoes em algumas
            // versoes de navegador, e a sugestao aqui vale mais.
            autoComplete={col.datalist ? undefined : 'off'}
            spellCheck={false}
            enterKeyHint="next"
            aria-label={col.header}
            aria-invalid={draftError || undefined}
            aria-describedby={draftError ? draftErrorId : undefined}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(false);
            }}
            onKeyDown={handleEditorKeyDown}
            onBlur={cancelEdit}
          />
          {col.datalist ? (
            <datalist id={listId}>
              {col.datalist.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          ) : null}
        </>
      );
    }

    switch (col.editor) {
      case 'checkbox': {
        const on = /^(1|true|sim)$/i.test(inputTextOf(col, row).trim());
        return (
          <button
            type="button"
            className={`grid-toggle${on ? ' grid-toggle--on' : ''}`}
            tabIndex={-1}
            aria-pressed={on}
            disabled={readOnly}
            title={on ? 'Linha ativa — clique para desativar' : 'Linha inativa — clique para ativar'}
            onClick={() => activateCell(r, c)}
          >
            {on ? '☑' : '☐'}
          </button>
        );
      }
      case 'grain': {
        const g = Math.max(0, Math.min(2, Number(inputTextOf(col, row)) || 0));
        return (
          <button
            type="button"
            className={`grid-grain grid-grain--${g}`}
            tabIndex={-1}
            disabled={readOnly}
            title={`${GRAIN_TITLE[g]} — clique para trocar`}
            aria-label={GRAIN_TITLE[g]}
            onClick={() => activateCell(r, c)}
          >
            {GRAIN_GLYPH[g]}
          </button>
        );
      }
      case 'color': {
        const hex = inputTextOf(col, row).trim() || '#FFFFFF';
        const open = colorCell === `${r}:${c}`;
        return (
          <>
            <button
              type="button"
              className="grid-swatch"
              tabIndex={-1}
              disabled={readOnly}
              style={{ background: hex }}
              title={`Cor da peça (${hex}) — clique para trocar`}
              aria-label={`Cor da peça ${hex}`}
              onClick={() => activateCell(r, c)}
            />
            {open ? (
              <div
                className="grid-palette"
                role="dialog"
                aria-label="Escolher cor da peça"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setColorCell(null);
                    wantFocus.current = true;
                  }
                }}
              >
                {PART_COLORS.map((hexOption) => (
                  <button
                    key={hexOption}
                    type="button"
                    className={`grid-palette__dot${
                      hexOption.toLowerCase() === hex.toLowerCase() ? ' is-current' : ''
                    }`}
                    style={{ background: hexOption }}
                    title={hexOption}
                    aria-label={`Cor ${hexOption}`}
                    onClick={() => {
                      applyPatch(r, col.parse(hexOption, row));
                      setColorCell(null);
                      wantFocus.current = true;
                    }}
                  />
                ))}
              </div>
            ) : null}
          </>
        );
      }
      case 'edgebanding': {
        const mask = inputTextOf(col, row).padEnd(4, '0').slice(0, 4);
        const sides = BAND_SIDES.filter((_s, i) => mask[i] === '1');
        const label = sides.length === 0 ? 'Sem fita de borda' : `Fita: ${sides.join(', ')}`;
        return (
          <button
            type="button"
            className="grid-band"
            tabIndex={-1}
            disabled={readOnly}
            title={`${label} — clique para editar`}
            aria-label={label}
            onClick={() => activateCell(r, c)}
          >
            <span
              className="grid-band__box"
              data-top={mask[0] === '1' ? '1' : undefined}
              data-right={mask[1] === '1' ? '1' : undefined}
              data-bottom={mask[2] === '1' ? '1' : undefined}
              data-left={mask[3] === '1' ? '1' : undefined}
            />
          </button>
        );
      }
      default: {
        const text = col.format(row);
        return <span className="grid-text">{text === '' ? ' ' : text}</span>;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // RENDER — GRADE
  // ---------------------------------------------------------------------------
  const draftErrorCol =
    editing && draftError && activeC < columns.length ? columns[activeC] : null;

  return (
    <div className="grid-wrap">
      <div
        className="grid"
        role="grid"
        aria-label={ariaLabel}
        aria-rowcount={rowCount + 1}
        aria-colcount={colCount + 2}
        onKeyDown={handleGridKeyDown}
        onPaste={handlePaste}
        onMouseLeave={() => emitHover(null)}
      >
        <div className="grid-head" role="rowgroup">
          <div className="grid-row grid-row--head" role="row" aria-rowindex={1} style={{ gridTemplateColumns: template }}>
            <div className="grid-cell grid-cell--sel" role="columnheader" aria-colindex={1}>
              <input
                type="checkbox"
                className="grid-checkbox"
                tabIndex={-1}
                checked={allSelected}
                onChange={toggleSelectAll}
                aria-label="Selecionar todas as linhas"
                title="Selecionar todas as linhas"
              />
            </div>
            {columns.map((col, c) => (
              <div
                key={col.key}
                className={`grid-cell grid-cell--head grid-cell--${col.align ?? 'start'}`}
                role="columnheader"
                aria-colindex={c + 2}
                title={col.title ?? col.header}
              >
                {col.header}
              </div>
            ))}
            <div
              className="grid-cell grid-cell--head grid-cell--act"
              role="columnheader"
              aria-colindex={colCount + 2}
            >
              Ações
            </div>
          </div>
        </div>

        <div className="grid-body" role="rowgroup">
          {rowCount === 0 ? (
            <div className="grid-empty" role="row">
              <div role="gridcell" aria-colindex={1}>
                {emptyMessage}
              </div>
            </div>
          ) : null}

          {rows.map((row, r) => {
            const id = getRowId(row);
            const issues = rowIssues ? rowIssues[id] : undefined;
            const invalid = issues !== undefined && issues.length > 0;
            const classes = ['grid-row'];
            if (invalid) classes.push('grid-row--invalid');
            if (highlightedRowId === id) classes.push('grid-row--highlight');
            if (usedSet.has(id)) classes.push('grid-row--used');
            if (selected.has(id)) classes.push('grid-row--selected');
            if (r === activeR) classes.push('grid-row--active');

            return (
              <div
                key={id}
                className={classes.join(' ')}
                role="row"
                aria-rowindex={r + 2}
                aria-selected={selected.has(id)}
                style={{ gridTemplateColumns: template }}
                onMouseEnter={() => emitHover(id)}
                title={invalid ? (issues as string[]).join('\n') : undefined}
              >
                <div className="grid-cell grid-cell--sel" role="gridcell" aria-colindex={1}>
                  <input
                    type="checkbox"
                    className="grid-checkbox"
                    tabIndex={-1}
                    checked={selected.has(id)}
                    onChange={() => toggleRowSelection(id)}
                    aria-label={`Selecionar a linha ${r + 1}`}
                  />
                </div>

                {columns.map((col, c) => {
                  const cellError = col.validate ? col.validate(row) : null;
                  const isActiveCell = r === activeR && c === activeC;
                  const isEditingCell = editing && isActiveCell;
                  const cellErrorId = cellError ? `${domId}-cell-${r}-${c}` : undefined;
                  const cellClasses = ['grid-cell', `grid-cell--${col.align ?? 'start'}`];
                  if (cellError) cellClasses.push('grid-cell--error');
                  if (isActiveCell) cellClasses.push('grid-cell--active');
                  if (isEditingCell) cellClasses.push('grid-cell--editing');
                  if (col.editable === false) cellClasses.push('grid-cell--readonly');
                  return (
                    <div
                      key={col.key}
                      ref={(el) => registerCell(`${r}:${c}`, el)}
                      className={cellClasses.join(' ')}
                      role="gridcell"
                      aria-colindex={c + 2}
                      aria-invalid={cellError ? true : undefined}
                      aria-describedby={cellErrorId}
                      aria-readonly={col.editable === false || undefined}
                      title={cellError ?? undefined}
                      tabIndex={isActiveCell ? 0 : -1}
                      onMouseDown={() => {
                        if (!(editing && isActiveCell)) focusCell(r, c);
                      }}
                      onDoubleClick={() => activateCell(r, c)}
                      onClick={() => {
                        if (isToggleEditor(col.editor)) return; // o proprio botao trata
                        if (isActiveCell && !editing) startEdit(r, c);
                      }}
                    >
                      {renderCellContent(row, r, col, c)}
                      {cellError ? (
                        <span className="sr-only" id={cellErrorId}>
                          {cellError}
                        </span>
                      ) : null}
                    </div>
                  );
                })}

                <div
                  className="grid-cell grid-cell--act"
                  role="gridcell"
                  aria-colindex={colCount + 2}
                >
                  <button
                    type="button"
                    className="grid-act"
                    tabIndex={-1}
                    title="Duplicar esta linha (Ctrl+Shift+D)"
                    aria-label={`Duplicar a linha ${r + 1}`}
                    onClick={() => onDuplicateRows([id])}
                  >
                    ⧉
                  </button>
                  <button
                    type="button"
                    className="grid-act grid-act--danger"
                    tabIndex={-1}
                    title="Remover esta linha (Ctrl+Del)"
                    aria-label={`Remover a linha ${r + 1}`}
                    onClick={() => onDeleteRows([id])}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {draftErrorCol ? (
        <p className="grid-msg" id={draftErrorId} role="alert">
          {errorHintOf(draftErrorCol)}
        </p>
      ) : null}
    </div>
  );
}
